import React from 'react';
import { X, MapPin, Building2, Mail, Hash } from 'lucide-react';

export interface LocationOption {
  id: string;
  codice: string;
  nominativo: string;
  indirizzo: string;
  email: string; // mascherata
  categoria?: string;
}

interface LocationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (locationId: string) => void;
  locations: LocationOption[];
  loading?: boolean;
}

/**
 * Modale per la selezione della sede quando un'impresa ha più sedi (stessa P.IVA)
 */
const LocationSelectionModal: React.FC<LocationSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  locations,
  loading = false
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Building2 className="w-6 h-6 text-white" />
                <h2 className="text-xl font-bold text-white">
                  Seleziona la Sede
                </h2>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-blue-100 text-sm mt-2">
              La tua Partita IVA è associata a più sedi. Seleziona quella a cui vuoi ricevere il codice OTP.
            </p>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <div className="space-y-3">
              {locations.map((location) => (
                <button
                  key={location.id}
                  onClick={() => onSelect(location.id)}
                  disabled={loading}
                  className="w-full text-left p-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl
                           hover:border-blue-500 dark:hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20
                           transition-all duration-200 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 
                                  dark:from-blue-900/50 dark:to-purple-900/50 rounded-lg 
                                  flex items-center justify-center group-hover:from-blue-200 group-hover:to-purple-200
                                  dark:group-hover:from-blue-800/50 dark:group-hover:to-purple-800/50 transition-colors">
                      <MapPin className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>

                    {/* Info */}
                    <div className="flex-grow min-w-0">
                      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1 truncate">
                        {location.nominativo}
                      </h3>
                      
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Hash className="w-4 h-4 flex-shrink-0" />
                          <span>Codice: {location.codice}</span>
                          {location.categoria && (
                            <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                              {location.categoria}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{location.indirizzo}</span>
                        </div>
                        
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                          <Mail className="w-4 h-4 flex-shrink-0" />
                          <span>OTP verrà inviato a: <strong>{location.email}</strong></span>
                        </div>
                      </div>
                    </div>

                    {/* Arrow indicator */}
                    <div className="flex-shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-800/50">
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
              Dopo aver selezionato la sede, riceverai il codice OTP all'indirizzo email associato.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationSelectionModal;

